<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted } from "vue";
import Button from "@/components/Button.vue";
import Input from "@/components/Input.vue";
import Modal from "@/components/Modal.vue";
import DataTable from "@/components/DataTable.vue";
import {
  CameraIcon,
  CheckCircleIcon,
  TrashIcon,
} from "@heroicons/vue/24/outline";
import services from "@/services";
import { useUserDataStore } from "@/stores/userDataStore";
import { useToaster } from "ram-components";

const userDataStore = useUserDataStore();
const isLoading = ref(false);
const isSubmitting = ref(false);

const parcelList = ref<any[]>([]);

const searchConsignment = ref("");
const searchCompany = ref("");
const searchContact = ref("");
const searchDate = ref("");

const selectedParcelIds = ref<string[]>([]);
const showHandoverModal = ref(false);
const collectorName = ref("");
const collectorContact = ref("");
const signature = ref("");
const idImageData = ref("");

const signatureCanvas = ref<HTMLCanvasElement | null>(null);
const isDrawing = ref(false);
let context: CanvasRenderingContext2D | null = null;

const showCamera = ref(false);
const videoElement = ref<HTMLVideoElement | null>(null);
const photoCanvas = ref<HTMLCanvasElement | null>(null);
let mediaStream: MediaStream | null = null;

const columns = [
  { key: "consignmentID", label: "Consignment Id" },
  { key: "recordDT", label: "Consigned DT" },
  { key: "shipperReference", label: "Shipper Reference" },
  { key: "receiverName", label: "Receiver Name" },
  { key: "receiverContactNumber", label: "Receiver Telephone" },
  { key: "specialInstructions", label: "Special Instructions" },
  { key: "ageDays", label: "Age (Days)" },
];

const toaster = useToaster({
  position: "bottom-right",
  closeOnClick: false,
  pauseOnHover: true,
  showCloseOnHover: true,
  timeout: 6000,
});

const loadParcels = async () => {
  isLoading.value = true;
  try {
    const payload = {
      consignmentID: searchConsignment.value.trim(),
      receiverName: searchCompany.value.trim(),
      receiverContactNumber: searchContact.value.trim(),
      recordDT: searchDate.value
        ? new Date(searchDate.value).toISOString()
        : null,
    };

    const response = await services.getParcelsInHubDetails(payload);

    if (response.data && Array.isArray(response.data.result)) {
      parcelList.value = response.data.result.map((parcel: any) => ({
        ...parcel,
        recordDT: parcel.recordDT ? parcel.recordDT.split("T")[0] : "",
      }));
    } else {
      parcelList.value = [];
    }
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load Parcels!",
    });

    parcelList.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch([searchConsignment, searchCompany, searchContact, searchDate], () => {
  loadParcels();
});

const handleSelect = (ids: string[]) => {
  selectedParcelIds.value = ids;
};

const openHandoverModal = () => {
  if (selectedParcelIds.value.length === 0) {
    toaster.push({
      type: "error",
      text: "Please select at least one parcel to handover",
    });

    return;
  }
  showHandoverModal.value = true;

  setTimeout(initCanvas, 100);
};

const closeHandoverModal = () => {
  showHandoverModal.value = false;
  selectedParcelIds.value = [];

  collectorName.value = "";
  collectorContact.value = "";
  signature.value = "";
  idImageData.value = "";
  stopCamera();
};

const handleHandover = async () => {
  if (!collectorName.value || !collectorContact.value || !signature.value) {
    return;
  }

  isSubmitting.value = true;

  try {
    const userId = Number(localStorage.getItem("activeUserID")) || 0;

    const payload = {
      consignmentIDs: selectedParcelIds.value,
      collectorName: collectorName.value,
      collectorContactNumber: collectorContact.value,
      collectorIDImageBase64: idImageData.value,
      collectorSignatureImageBase64: signature.value,
      createdBy: userId,
    };

    const response = await services.saveConfirmCollection(payload);

    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.status === "SUCCEEDED")
    ) {
      closeHandoverModal();
      selectedParcelIds.value = [];
      await loadParcels();
      toaster.push({
        type: "success",
        text: response.data.message,
      });
    }
  } catch (error: any) {
    toaster.push({
      type: "success",
      text: error.response?.data?.message,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const initCanvas = () => {
  if (signatureCanvas.value) {
    context = signatureCanvas.value.getContext("2d");
    if (context) {
      context.strokeStyle = "#000";
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";
    }
  }
};

const startDrawing = (event: MouseEvent | TouchEvent) => {
  isDrawing.value = true;
  const rect = signatureCanvas.value?.getBoundingClientRect();
  if (!rect || !context) return;

  const x =
    "touches" in event
      ? event.touches[0].clientX - rect.left
      : event.clientX - rect.left;
  const y =
    "touches" in event
      ? event.touches[0].clientY - rect.top
      : event.clientY - rect.top;

  context.beginPath();
  context.moveTo(x, y);
};

const draw = (event: MouseEvent | TouchEvent) => {
  if (!isDrawing.value || !context) return;
  const rect = signatureCanvas.value?.getBoundingClientRect();
  if (!rect) return;

  const x =
    "touches" in event
      ? event.touches[0].clientX - rect.left
      : event.clientX - rect.left;
  const y =
    "touches" in event
      ? event.touches[0].clientY - rect.top
      : event.clientY - rect.top;

  context.lineTo(x, y);
  context.stroke();

  // Prevent scrolling on mobile while signing
  if (event.type === "touchmove") event.preventDefault();
};

const stopDrawing = () => {
  if (isDrawing.value && signatureCanvas.value) {
    isDrawing.value = false;
    signature.value = signatureCanvas.value.toDataURL();
  }
};

const clearSignature = () => {
  if (signatureCanvas.value && context) {
    context.clearRect(
      0,
      0,
      signatureCanvas.value.width,
      signatureCanvas.value.height,
    );
    signature.value = "";
  }
};
const facingMode = ref<"user" | "environment">("user"); // "user" = front, "environment" = back

const startCamera = async () => {
  showCamera.value = true;
  idImageData.value = "";

  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
  }

  try {
    const constraints = {
      video: {
        facingMode: facingMode.value,
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    };

    mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream;
      videoElement.value.play();
    }
  } catch (err) {
    toaster.push({
      type: "error",
      text: "Could not access camera. Ensure permissions are granted.",
    });
    showCamera.value = false;
  }
};

const toggleCamera = () => {
  facingMode.value = facingMode.value === "user" ? "environment" : "user";
  startCamera();
};

const stopCamera = () => {
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }
  showCamera.value = false;
};

const captureIdImage = () => {
  if (!videoElement.value || !photoCanvas.value) return;

  const video = videoElement.value;
  const canvas = photoCanvas.value;
  const context = canvas.getContext("2d");

  if (context) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    idImageData.value = canvas.toDataURL("image/jpeg", 0.8);
    stopCamera();
  }
};

const retakeIdImage = () => {
  idImageData.value = "";
  startCamera();
};

onMounted(() => {
  userDataStore.setLoading(false);
  loadParcels();
});

onUnmounted(() => {
  stopCamera();
});
</script>

<template>
  <div>
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Parcel Collections</h1>
      <p class="mt-1 text-gray-500">
        Find and process parcels ready for collection.
      </p>
    </div>

    <div class="mb-6 rounded-xl border border-gray-200 bg-white p-4">
      <div class="grid gap-4 md:grid-cols-4">
        <Input v-model="searchConsignment" placeholder="Consignment ID" />
        <Input v-model="searchCompany" placeholder="Receiver Name" />
        <Input
          v-model="searchContact"
          placeholder="Contact Number"
          v-limit="10"
        />
        <Input v-model="searchDate" type="date" placeholder="dd/mm/yyyy" />
      </div>
    </div>

    <div class="rounded-xl border border-gray-200 bg-white">
      <div
        class="flex items-center justify-between border-b border-gray-200 p-4"
      >
        <div>
          <h2 class="font-semibold text-gray-900">
            Parcels in Hub ({{ parcelList.length }})
          </h2>
        </div>
        <Button
          @click="openHandoverModal"
          :disabled="selectedParcelIds.length === 0"
          variant="ram"
        >
          Handover ({{ selectedParcelIds.length }})
        </Button>
      </div>

      <DataTable
        :columns="columns"
        :data="parcelList"
        :loading="isLoading"
        selectable
        :selected-ids="selectedParcelIds"
        @select="handleSelect"
        row-key="consignmentID"
        empty-message="No parcels found matching criteria."
      />
    </div>

    <Modal
      :is-open="showHandoverModal"
      title="Confirm Collection"
      size="lg"
      @close="closeHandoverModal"
    >
      <div class="space-y-4">
        <div class="rounded-lg bg-gray-50 p-3">
          <p class="mb-2 text-sm text-gray-600">
            You are handing over {{ selectedParcelIds.length }} parcel(s):
          </p>
          <div class="max-h-20 overflow-y-auto">
            <ul class="space-y-1">
              <li
                v-for="id in selectedParcelIds"
                :key="id"
                class="font-mono text-xs text-gray-800"
              >
                {{ id }}
              </li>
            </ul>
          </div>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <div class="space-y-4">
              <div>
                <FormKit
                  v-model="collectorName"
                  type="text"
                  label="Collector's Name*"
                  placeholder="Enter name"
                  label-class="block text-sm font-medium text-gray-700 mb-1"
                  input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
                />
              </div>
              <div>
                <FormKit
                  v-model="collectorContact"
                  type="text"
                  label="Contact Number*"
                  v-limit="10"
                  placeholder="Enter contact number"
                  validation="required|matches:/^[0-9]{10}$/"
                  :validation-messages="{
                    matches: 'Contact number must be exactly 10 digits',
                    required: 'Contact number is required',
                  }"
                  label-class="block text-sm font-medium text-gray-700 mb-1"
                  input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
                  messages-class="text-sm text-red-500 mt-1"
                />
              </div>
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700"
              >Collector's Id*</label
            >

            <Button
              v-if="!showCamera && !idImageData"
              variant="secondary"
              class="w-full justify-center"
              @click="startCamera"
            >
              <CameraIcon class="mr-2 h-5 w-5" />
              Capture ID
            </Button>

            <Button
              v-else-if="idImageData"
              variant="secondary"
              class="w-full justify-center text-red-600 hover:text-red-700"
              @click="retakeIdImage"
            >
              <TrashIcon class="mr-2 h-5 w-5" />
              Retake Photo
            </Button>

            <div
              class="relative flex h-40 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-gray-300 bg-gray-100"
            >
              <video
                v-if="showCamera"
                ref="videoElement"
                autoplay
                playsinline
                class="h-full w-full object-cover"
              ></video>
              <button
                v-if="showCamera"
                @click="toggleCamera"
                class="absolute right-2 top-2 z-20 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
                title="Switch Camera"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="h-5 w-5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
              </button>
              <img
                v-else-if="idImageData"
                :src="idImageData"
                alt="Captured ID"
                class="h-full w-full object-contain"
              />

              <p v-else class="px-4 text-center text-xs text-gray-400">
                Camera not active
              </p>

              <canvas ref="photoCanvas" class="hidden"></canvas>
            </div>

            <Button
              v-if="showCamera"
              variant="ram"
              class="w-full justify-center"
              @click="captureIdImage"
            >
              <CameraIcon class="mr-2 h-5 w-5" />
              Take Photo
            </Button>
          </div>
        </div>

        <div>
          <div class="mb-2 flex items-center justify-between">
            <label class="text-sm font-medium text-gray-700"
              >Collector's Signature*</label
            >
            <button
              @click="clearSignature"
              class="text-xs font-medium text-red-600 hover:underline"
            >
              Clear Signature
            </button>
          </div>
          <div
            class="h-32 touch-none rounded-lg border-2 border-gray-300 bg-white"
          >
            <canvas
              ref="signatureCanvas"
              class="block h-full w-full cursor-crosshair"
              width="500"
              height="150"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseleave="stopDrawing"
              @touchstart.prevent="startDrawing"
              @touchmove.prevent="draw"
              @touchend.prevent="stopDrawing"
            ></canvas>
          </div>
          <p
            v-if="signature"
            class="mt-1 flex items-center gap-1 text-xs text-green-600"
          >
            <CheckCircleIcon class="h-4 w-4" /> Signature captured.
          </p>
        </div>
      </div>

      <template #footer>
        <Button variant="secondary" @click="closeHandoverModal">
          Cancel
        </Button>
        <Button
          @click="handleHandover"
          :loading="isSubmitting"
          variant="ram"
          :disabled="
            !collectorName ||
            !/^[0-9]{10}$/.test(collectorContact) ||
            !signature ||
            !idImageData
          "
        >
          Confirm Handover
        </Button>
      </template>
    </Modal>
  </div>
</template>
